/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { appHideLoader, appShowLoader } from '@actions/app.action';
import { paymentSubmitPayments } from '@actions/payment.action';
import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { PaymentForm } from '@models/form/form.models';
import { PaymentFlow } from '@models/payment.model';
import { Store } from '@ngrx/store';
import { selectPaymentOrderInfo } from '@selectors/payment.selector';
import { EnvironmentService } from '@services/environment-service/environment.service';
import { BehaviorSubject, filter, map, take, tap } from 'rxjs';
import { UpliftErrorType, UpliftOrderInfo, UpliftPath, UpliftPaymentData, UpliftPaymentMode, UpliftResponse, UpliftStatus } from 'src/app/typings/uplift';

@Injectable({
  providedIn: 'root'
})
export class UpliftService {
  // eslint-disable-next-line no-useless-escape
  private upliftInitScript: string = "(function (u, p, l, i, f, t, o, b, j) { u['UpLiftPlatformObject'] = f; u[f] = u[f] || function () { (u[f].q = u[f].q || []).push(arguments) }, u[f].l = 1 * new Date(); b = p.createElement(l), j = p.getElementsByTagName(l)[0]; b.async = 1; b.src = i + '?upcode=' + t; j.parentNode.insertBefore(b, j); o = u.location.host.match(/[\w-]+\.\w{2,3}(:\d+)?$/); if (o) o = o[0]; u[f]('create', t, o) })(window, document, 'script', 'https://tag.uplift.com/external/up.js', 'up', 'upInitCode');"
  private scriptLoaded = false;
  private upliftInitialized = false;
  private initializedPath: UpliftPath = 'main';
  private orderInfo$ = this.store.select(selectPaymentOrderInfo);
  private upliftLoanStatus$ = new BehaviorSubject<UpliftStatus>('SERVICE_UNAVAILABLE');
  private selectedUpliftPaymentModel: UpliftPaymentMode | null | undefined;

  constructor(@Inject(DOCUMENT) private document: Document, private store: Store) { }

  get isUpliftInitialized() {
    return this.upliftInitialized;
  }

  onChangeUpliftLoanStatus$() {
    return this.upliftLoanStatus$.asObservable()
  }

  loadUpliftLib() {
    if(!this.scriptLoaded) {
      this.upliftInitScript = this.upliftInitScript.replace('upInitCode', EnvironmentService.environment.uplift.upCode);
      const script = this.document.getElementById('uplift-script') as HTMLScriptElement;
      if (script) {
        script.text = this.upliftInitScript;
      }
      this.scriptLoaded = true;
    }
  }

  waitForUpliftObject(): Promise<void> {
    return new Promise<void>((resolve) => {
      const intervalId = setInterval(() => {
        if (window.Uplift !== undefined) {
          clearInterval(intervalId);
          resolve();
        }
      }, 100);
    });
  }

  initUpliftIFrame(currencyLocale: string, currency: string, isMobile: boolean, path: UpliftPath) {
    if (this.upliftInitialized && this.initializedPath != path) {
      window.Uplift.Payments.clear();
      this.upliftInitialized = false;
    }

    if (!this.upliftInitialized) {
      window.Uplift.Payments.init({
        apiKey: EnvironmentService.environment.uplift.apiKey,
        locale: currencyLocale,
        currency: currency,
        checkout: true,
        channel: isMobile ? "mobile" : "desktop",
        container: "#up-pay-monthly-container",
        onChange: this.upliftChanged,
        path: path
      });
      this.upliftInitialized = true;
      this.initializedPath = path;
    }
  }

  waitForUpliftInitialization(): Promise<void> {
    if (window.Uplift?.initialized) {
      return new Promise<void>((resolve) => {
        resolve();
      });
    }
    return new Promise<void>((resolve) => {
      const intervalId = setInterval(() => {
        if (window.Uplift?.initialized) {
          this.upliftLoanStatus$.next('OFFER_UNAVAILABLE')
          clearInterval(intervalId);
          resolve();
        }
      }, 100);
    });
  }

  loadPaymentOrder(orderInfo: UpliftOrderInfo) {
    window.Uplift.Payments.load(orderInfo);
  }

  setupUpliftIFrame(flow: PaymentFlow | null) {
    if (flow === null) {
      if (this.selectedUpliftPaymentModel === 'pay monthly') {
        window.Uplift.Payments.deselect('other');
        window.Uplift.Analytics.selectPayment('other')
      }
      this.selectedUpliftPaymentModel = null;
      return;
    }

    if (flow === PaymentFlow.UPLIFT_LOAN) {
      window.Uplift.Payments.select();
      this.selectedUpliftPaymentModel = 'pay monthly';
      return
    }

    if (flow === PaymentFlow.CARD) {
      if (this.selectedUpliftPaymentModel === 'pay monthly') {
        window.Uplift.Payments.deselect('full');
        window.Uplift.Analytics.selectPayment('full');
      }

      this.selectedUpliftPaymentModel = 'full'
    }
  }

  deselectUplift() {
    if (this.selectedUpliftPaymentModel === 'pay monthly') {
      window.Uplift.Payments.deselect('other');
      window.Uplift.Analytics.selectPayment('other')
    }
    this.selectedUpliftPaymentModel = null
  }

  requestVCCData() {
    this.store.dispatch(appShowLoader());
    window.Uplift.Payments.getToken();
  }

  error(msg: string, errorType: UpliftErrorType) {
    window.Uplift.Payments.error(msg, errorType);
  }

  confirmPayment(confirmationId: string) {
    window.Uplift.Analytics.orderResponse({
      mode_of_payment: 'pay monthly',
      order_id: confirmationId
    });
    window.Uplift.Payments.confirm(confirmationId);
    this.upliftLoanStatus$.next('OFFER_UNAVAILABLE');
    window.Uplift.Payments.clear();
    this.upliftInitialized = false;
  }

  private upliftChanged = (response: UpliftResponse) => {
    this.upliftLoanStatus$.next(response.status);
    if (response.status === 'OFFER_UNAVAILABLE' || response.status === 'SERVICE_UNAVAILABLE') {
      this.deselectUplift();
    }
    if (response.reasons?.length ?? 0 > 0 ) {
      this.store.dispatch(appHideLoader());
    }
    if (response.token) {
      this.orderInfo$
      .pipe(
        filter(orderInfo => !!orderInfo),
        take(1),
        tap(orderInfo => {
          const paymentForm: UpliftPaymentData = { mode_of_payment: 'pay monthly', payment_options:['full', 'pay monthly'] };
          window.Uplift.Analytics.orderSubmit(orderInfo!, paymentForm)
        }),
        map(orderInfo => {
          const paymentForm: PaymentForm = {
            amount: orderInfo!.order_amount / 100,
            billingAddressLineOne: response.token!.contact.street_address,
            billingAddressLineTwo: '',
            billingCity: response.token!.contact.city,
            billingCountry: response.token!.contact.country,
            billingState: response.token!.contact.region,
            billingZipCode: response.token!.contact.postal_code,
            cardCvv: response.token!.card_ccv,
            cardExpirationMonth: response.token!.expiration_month,
            cardExpirationYear: response.token!.expiration_year,
            cardNumber: response.token!.card_number,
            nameOnCard: response.token!.name_on_card,
            useMultiplePayments: false,
            email: response.token!.contact.email,
            phone: response.token!.contact.phone,
            phoneCode: 'US'
          }
          return paymentForm;
        }),
        tap(() => {
          this.store.dispatch(appHideLoader());
        })
      ).subscribe(paymentForm => {
        this.store.dispatch(
          paymentSubmitPayments({
            payments: [paymentForm],
          }),
        );
      })
    }
  }
}
